import * as Sentry from "@sentry/react";

export interface ReportHandledErrorOptions {
  /** Searchable tags attached to the event, e.g. `{ code: "conversation_not_found" }`. */
  context?: Record<string, string>;
  /**
   * Whether the error's own `.message` is safe to send verbatim. Defaults to `false`: a caught error
   * is reported using only its name/constructor plus `context`, never its message.
   *
   * This is deliberately opt-in rather than opt-out. It exists because of a concrete case: collect-ui's
   * `invalid_answer` errors carry a Pydantic `ValidationError` message that can embed a respondent's
   * actual typed answer (up to ~2000 chars) via `input_value=...`. A caller must explicitly assert
   * `includeMessage: true` after confirming the message can never contain user-entered content —
   * the unsafe case (the default) is the one that can't accidentally leak text into Sentry.
   */
  includeMessage?: boolean;
}

/**
 * Reports an error that application code has already caught and handled (shown a message, reset some
 * state, recovered) but that the team should still know happened — as opposed to `captureException`,
 * which is for errors that were never handled at all.
 *
 * Errors caught this way never reach `MutationCache`/`QueryCache`'s cache-level `onError` (wired via
 * `monitoredQueryClientConfig()`): that hook only fires when the underlying query/mutation function
 * itself rejects. An error thrown by *application code* after a network call already succeeded — e.g.
 * a business-level failure encoded in a 200 OK response body — never touches it, and is otherwise
 * invisible to Sentry unless reported explicitly here.
 */
export function reportHandledError(error: unknown, options?: ReportHandledErrorOptions): void {
  const hint = options?.context ? { tags: options.context } : undefined;

  if (options?.includeMessage) {
    Sentry.captureException(error, hint);
    return;
  }

  const name = error instanceof Error ? error.name : typeof error;
  const safeError = new Error(`Handled error (message omitted): ${name}`);
  safeError.name = name;
  if (error instanceof Error && error.stack) {
    // Preserve the real stack trace (call-site info, not free text) for debuggability.
    safeError.stack = error.stack;
  }
  Sentry.captureException(safeError, hint);
}
